<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.mailAccounts',
    path: 'mail-accounts',
    component: 'MailAccountConfigurations',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MailAccountConfigurations',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const openMailAccountConfig = (...args) => {
    const [oid] = args;

    return $imodal.value
      .show({
        name: 'MailAccountConfig',
        props: { oid },
      })
      .then((res) => loadData('items'));
  };

  const objectType = ref('mail-account');
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_editable = computed(() => false);
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'id',
      header: $fn.value.pgettext('MailAccountConfig', 'ID'),
      width: '180px',
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <a class="text-nowrap"\n\
     href="#"\n\
     @click.prevent="globs.openMailAccountConfig(row.id)"\n\
  >\n\
    {{ value }}\n\
  </a>\n\
</div>\n\
',
      },
    },
    {
      name: 'address',
      header: $fn.value.pgettext('MailAccountConfig', 'Address'),
      width: '180px',
    },
    {
      name: 'description',
      header: $fn.value.pgettext('MailAccountConfig', 'Description'),
      width: '200px',
    },
  ]);

  const $data = computed(() => ({
    objectType: objectType.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_editable: b_3343864469950044_editable.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_object_by_type', {
        parameters: { object_type: $data.value.objectType },
      })
      .then((items) => _.orderBy(items, ['created_at'], ['asc']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectType' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_editable', nodeId: '3343864469950044' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      objectType,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_editable,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_7og0zmr51_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6705964765214521_script_93ob9l0pr_value_callable = async (
    $event
  ) => {
    return openMailAccountConfig();
  };
  const act_6705964765214521_script_93ob9l0pr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_93ob9l0pr_value_callable($event);
      },
    };
  };
  const act_8116269939469002_showModal_vvcpogji9_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('import_object_by_type', {
          object_type: objectType.value,
        }),
        accept: '.json,.yaml,.yml',
      },
    };
  };
  const act_3343864469950044_script_56vzh48hz_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .delete({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_56vzh48hz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_56vzh48hz_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_7og0zmr51 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_7og0zmr51_getActionArgs,
    });
  const inst_evh_7315092382398562_close_wnkkoy9c7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_7og0zmr51,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_wnkkoy9c7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_18yb3pbl0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_rwnk5g597 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_18yb3pbl0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_rwnk5g597],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_s0tau0woi =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_tso96i5tl =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_s0tau0woi,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_tso96i5tl],
      event: 'click.prevent.stop',
    });
  const act_6705964765214521_script_93ob9l0pr = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_93ob9l0pr_getActionArgs,
  });
  const inst_evh_6705964765214521_clickPreventStop_zzefiaebq =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_93ob9l0pr,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_zzefiaebq],
      event: 'click.prevent.stop',
    });
  const act_8116269939469002_showModal_vvcpogji9 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8116269939469002_showModal_vvcpogji9_getActionArgs,
    });
  const inst_evh_8116269939469002_clickPreventStop_fqcbeg2bh =
    new actionTypes.EventHandlerImpl({
      action: act_8116269939469002_showModal_vvcpogji9,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8116269939469002_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8116269939469002_clickPreventStop_fqcbeg2bh],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_56vzh48hz = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_56vzh48hz_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_qyp1xls4f =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_56vzh48hz,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_qyp1xls4f],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_wnkkoy9c7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_rwnk5g597,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_tso96i5tl,
    reload: inst_evh_8320016629450276_clickPreventStop_tso96i5tl,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_6705964765214521_clickPreventStop_zzefiaebq,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_8116269939469002_clickPreventStop_fqcbeg2bh,
    inst_evh_8116269939469002_clickPreventStop,
    inst_evh_3343864469950044_delete_qyp1xls4f,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_wnkkoy9c7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_wnkkoy9c7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_rwnk5g597 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_rwnk5g597.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_tso96i5tl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_tso96i5tl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_zzefiaebq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_zzefiaebq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop_fqcbeg2bh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop_fqcbeg2bh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_qyp1xls4f = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_qyp1xls4f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MailAccountConfigurations"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3" v-if="loaded">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{
            pgettext(
              'MailAccountConfigurations',
              'Email account configurations'
            )
          }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('MailAccountConfigurations', 'Add account...')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('MailAccountConfigurations', 'Export')"
          variant="light"
          size="sm"
          :disabled="!items || !items.length"
          use-auth
          force-download
          downloadFileName="mail-account-configurations.json"
          :href="
            $fn.urlFor('export_object_by_type', { object_type: objectType })
          "
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('MailAccountConfigurations', 'Import')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_8116269939469002_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.MailAccountConfigurations"
        :editable="b_3343864469950044_editable"
        :value="items"
        :globs="{ openMailAccountConfig }"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
